import { useEffect } from 'react';
import dayjs from 'dayjs';
import { ListingSearchForm } from '../../components/Listing/ListingSearchForm';
import { ProductionHistoryConst } from '../../constants';
import { ReportConst, type ReportType } from '../../constants/ReportConst';
import { useItemFilters } from '../../contexts/ItemFilterContext';
import { useMasters } from '../../contexts/MastersContext';
import { useBiddingReportPrintMutation } from '../../hooks/useBiddingResultQuery';
import { useReportLogic } from '../../hooks/useReportLogic';
import { formatFromDateInput } from '../../utils/date';
import type { GetItemsFilters } from '../../types/GetItemsFilters';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

interface ListingPageProps {
  // 帳票の種類（'ichiran' | 'teawase' | 'rakusatsu'）
  reportType: ReportType;
  // ページヘッダーに表示するタイトル
  title: string;
}

/**
 * 出品一覧・帳票印刷ページコンポーネント
 * データの取得、フィルタリング、印刷対象の選択状態を管理。
 */
export const ListingPage = ({ reportType, title }: ListingPageProps) => {
  // --- Contexts ---
  const queryClient = useQueryClient();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading } = useMasters();

  /**
   * 初期化 Effect:
   * 入札日または出品区分が未設定の場合、デフォルト値をセット。
   */
  useEffect(() => {
    // すでに条件（入札日と出品区分の両方）がある場合は、
    // Form側の初期化ロジックを優先するため、ここでは何もしない
    if (itemFilters.biddingDate && itemFilters.prefectureType) {
      return;
    }

    // Contextが完全に空の場合（初回アクセス時など）のみ最小限の補完を行う
    if (!itemFilters.biddingDate || !itemFilters.prefectureType) {
      const today = dayjs().format('YYYY-MM-DD');
      
      updateFiltersAndNavigate({
        ...itemFilters,

        // 日付がなければ当日、あればそのまま保持
        biddingDate: itemFilters.biddingDate || formatFromDateInput(today),
        // 出品区分がなければデフォルトの 1 (県内) をセット
        prefectureType: itemFilters.prefectureType || 1,
      });
    }
  }, [itemFilters.biddingDate, itemFilters.prefectureType, updateFiltersAndNavigate]);

  // --- Queries / Mutations ---
  /** 出品一覧データの取得（TanStack Query） */
  const { mutateAsync: fetchPrintData, isPending: isFetching } = useBiddingReportPrintMutation();

  // --- Custom Hooks (Business Logic) ---
  /** フィルタリング、印刷用DTO変換、WebView2通信のロジックを一任 */
  const {
    handleExecutePrint,
    isPrinting,
  } = useReportLogic(reportType);

  const handlePrintClick = async (filters: GetItemsFilters) => {
    try {

      const safeFilters = {
        ...filters,
        prefectureType: Number(filters.prefectureType) || 1, // NaNの場合は1にする
      };

      // 1. フォームから受け取った「最新の filters」を渡して、APIを直接叩く
      const result = await fetchPrintData({ 
        ...safeFilters,
        productionHistory: ProductionHistoryConst.YES,
        isScheduleTypeConsidered: reportType === ReportConst.RAKUSATSU_ICHIRAN
      }as any);
      
      // 2. 取得したデータを直接印刷ロジックに流し込む（データがない場合は空配列を渡す）
      // ※ APIのレスポンスの形（resultそのままか、result.dataか）に合わせて調整してください
      const items = result || []; 
      handleExecutePrint(filters, items);

    } catch (error) {
      console.error("印刷用データの取得に失敗しました", error);
    }
  };

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  // --- Render (Guard) ---
  if (isMastersLoading) {
    return <div className="bg-white min-h-screen flex items-center justify-center font-bold">マスタ情報を読み込み中...</div>;
  }

  return (
    <div className="bg-white min-h-screen font-sans pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ヘッダーセクション */}
        <header className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">{title}
          </h1>
        </header>

        <main>
          {/* 検索フォーム */}
          <ListingSearchForm 
            key={reportType} // reportTypeの切替で別物として判断し初期化
            onExecute={handlePrintClick}
            isPrinting={isPrinting || isFetching}
            currentReportType={reportType}
          />
        </main>
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}